// mobile_v1: responsive layout — grid 2x2 บนมือถือ + card list แทนตาราง
import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { Trophy, Users, CheckCircle, Zap, Clock, TrendingUp, Calendar, Timer, MessageCircle, AlertTriangle } from 'lucide-react';
// timeline_v1: charts library
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; }; /* kpi_insights_v1 */
const medal = (i) => ['🥇','🥈','🥉'][i] || `${i+1}.`;
const fmtSec = (sec) => {
  if (sec == null) return '-';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec/60)}m ${Math.round(sec%60)}s`;
};

const StatCard = ({ icon: Icon, label, value, sub, color, isMobile, trend }) => ( /* kpi_insights_v1 */
  <div style={{
    background: '#fff',
    borderRadius: 14,
    padding: isMobile ? '14px 16px' : '20px 24px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? 10 : 16,
    borderLeft: `4px solid ${color}`,
    minWidth: 0,
  }}>
    <div style={{
      background: color + '18',
      borderRadius: 10,
      padding: isMobile ? 8 : 12,
      flexShrink: 0,
    }}>
      <Icon size={isMobile ? 18 : 22} color={color} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{
        fontSize: isMobile ? 22 : 26,
        fontWeight: 700,
        color: '#1e293b',
        lineHeight: 1.1,
      }}>{value}{trend && (
        <span style={{ fontSize: 11, fontWeight: 800, marginLeft: 8, padding: '2px 6px', borderRadius: 999, verticalAlign: 'middle',
          color: trend.good ? '#16a34a' : '#dc2626', background: trend.good ? '#f0fdf4' : '#fef2f2' }}>{trend.text}</span>
      )}</div>
      <div style={{
        fontSize: isMobile ? 12 : 13,
        color: '#64748b',
        marginTop: isMobile ? 2 : 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>{label}</div>
      {sub && (
        <div style={{
          fontSize: isMobile ? 10 : 11,
          color: '#94a3b8',
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>{sub}</div>
      )}
    </div>
  </div>
);

// mobile_v1: card row สำหรับมือถือ (แทนแถวในตาราง)
const AgentCard = ({ agent, idx }) => {
  const pct = Number(agent.fast_close_pct);
  const pctColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      padding: 14,
      borderTop: '1px solid #f1f5f9',
    }}>
      {/* Top row: medal + name + % */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 22, flexShrink: 0 }}>{medal(idx)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {agent.agent_name}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {agent.email}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: pctColor, lineHeight: 1.1 }}>
            {pct.toFixed(1)}%
          </div>
          <div style={{ width: 60, height: 4, background: '#f1f5f9', borderRadius: 2, marginTop: 4 }}>
            <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pctColor, borderRadius: 2 }} />
          </div>
        </div>
      </div>

      {/* Stats grid 2x2 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        <div style={{ background: '#f0f9ff', padding: '8px 10px', borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>รับเคส</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#0ea5e9' }}>{agent.total_cases}</div>
        </div>
        <div style={{ background: '#f0fdf4', padding: '8px 10px', borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>ปิดเคส</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#22c55e' }}>{agent.closed_cases}</div>
        </div>
        <div style={{ background: '#fefce8', padding: '8px 10px', borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: '#854d0e', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>⚡ ปิดไว 1 นาที</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#854d0e' }}>{agent.fast_close_cases}</div>
        </div>
        <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>เวลาตอบ</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={14} />{fmtSec(agent.avg_response_sec)}
          </div>
        </div>
        {/* accept_v2: 2 chips ใหม่ */}
        <div style={{ background: '#f0fdfa', padding: '8px 10px', borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: '#0d9488', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>รับเคสไว</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0d9488', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Timer size={14} />{Number(agent.avg_accept_sec) > 0 ? fmtSec(Math.round(Number(agent.avg_accept_sec))) : '-'}
          </div>
        </div>
        <div style={{ background: '#eef2ff', padding: '8px 10px', borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: '#4f46e5', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>ตอบไวหลังรับ</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#4f46e5', display: 'flex', alignItems: 'center', gap: 4 }}>
            <MessageCircle size={14} />{Number(agent.avg_reply_after_accept_sec) > 0 ? fmtSec(Math.round(Number(agent.avg_reply_after_accept_sec))) : '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function KPI() {
  const [from, setFrom] = useState(daysAgo(6)); /* kpi_insights_v1: default 7d */
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null); // timeline_v1
  const [auto, setAuto] = useState(null); /* kpi_automation_v1 */
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const load = async (qf, qt) => { /* kpi_insights_v1 */
    const _f = qf || from, _t = qt || to;
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        API.get(`/api/kpi?from=${_f}&to=${_t}`),
        API.get(`/api/kpi/timeline?from=${_f}&to=${_t}`),
        API.get(`/api/kpi/automation?from=${_f}&to=${_t}`)
      ]);
      setData(r1.data);
      setTimeline(r2.data); // timeline_v1
      setAuto(r3.data); /* kpi_automation_v1 */
    } catch { alert('โหลดข้อมูลไม่ได้'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  /* kpi_insights_v1 */
  const mkTrend = (cur, prev, higherIsBetter) => {
    if (!auto || !auto.trend) return null;
    const c = Number(cur) || 0, p = Number(prev) || 0;
    if (c === 0 && p === 0) return null;
    const diff = c - p;
    if (diff === 0) return { text: '±0', good: true };
    const chg = p > 0 ? Math.round(Math.abs(diff) / p * 100) : 100;
    const up = diff > 0;
    return { text: (up ? '▲ ' : '▼ ') + chg + '%', good: higherIsBetter ? up : !up };
  };
  const setPreset = (n, monthMode) => {
    const t = new Date();
    const toStr = t.toISOString().split('T')[0];
    let fromStr;
    if (monthMode) fromStr = new Date(t.getFullYear(), t.getMonth(), 1).toISOString().split('T')[0];
    else { const d = new Date(t); d.setDate(d.getDate() - (n - 1)); fromStr = d.toISOString().split('T')[0]; }
    setFrom(fromStr); setTo(toStr); load(fromStr, toStr);
  };

  const agents = data?.agents || [];
  const totalCases = agents.reduce((s,a) => s + Number(a.total_cases), 0);
  const totalFast = agents.reduce((s,a) => s + Number(a.fast_close_cases), 0);
  const totalClosed = agents.reduce((s,a) => s + Number(a.closed_cases), 0);
  const overallPct = totalClosed > 0 ? ((totalFast/totalClosed)*100).toFixed(1) : '0.0';
  // accept_v2: avg_accept + avg_reply_after_accept (weighted ตาม total_cases)
  const _avgAccept = (() => {
    const valid = agents.filter(a => Number(a.avg_accept_sec) > 0);
    if (!valid.length) return null;
    const sum = valid.reduce((s,a) => s + Number(a.avg_accept_sec) * Number(a.total_cases), 0);
    const count = valid.reduce((s,a) => s + Number(a.total_cases), 0);
    return count > 0 ? sum / count : null;
  })();
  const _avgReplyAfter = (() => {
    const valid = agents.filter(a => Number(a.avg_reply_after_accept_sec) > 0);
    if (!valid.length) return null;
    const sum = valid.reduce((s,a) => s + Number(a.avg_reply_after_accept_sec) * Number(a.total_cases), 0);
    const count = valid.reduce((s,a) => s + Number(a.total_cases), 0);
    return count > 0 ? sum / count : null;
  })();

  return (
    <div style={{
      padding: isMobile ? '16px 12px' : '24px 28px',
      background: '#f8fafc',
      minHeight: '100%',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Trophy size={isMobile ? 22 : 26} color="#f59e0b" />
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b' }}>
              KPI แอดมิน
            </h1>
            <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#64748b' }}>
              วัดผลการตอบสนองและปิดเคส
            </p>
          </div>
        </div>

        {/* Date picker */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#fff',
          padding: isMobile ? '10px 12px' : '10px 14px',
          borderRadius: 10,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          flexWrap: 'wrap',
        }}>
          <Calendar size={16} color="#64748b" style={{ flexShrink: 0 }} />
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: isMobile ? 14 : 13,
              color: '#1e293b',
              minWidth: 0,
              flex: isMobile ? '1 1 110px' : 'none',
              padding: 0,
              background: 'transparent',
            }} />
          <span style={{ color: '#94a3b8' }}>—</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: isMobile ? 14 : 13,
              color: '#1e293b',
              minWidth: 0,
              flex: isMobile ? '1 1 110px' : 'none',
              padding: 0,
              background: 'transparent',
            }} />
          <button onClick={load} disabled={loading}
            style={{
              background: '#0ea5e9',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: isMobile ? '8px 16px' : '6px 14px',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: 600,
              flexShrink: 0,
            }}>
            {loading ? '...' : 'ดู'}
          </button>
          {/* kpi_insights_v1: quick presets */}
          {[['วันนี้', () => setPreset(1)], ['7 วัน', () => setPreset(7)], ['30 วัน', () => setPreset(30)], ['เดือนนี้', () => setPreset(0, true)]].map(([lab, fn]) => (
            <button key={lab} onClick={fn} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, padding: isMobile ? '8px 12px' : '6px 12px', fontSize: 12.5, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>{lab}</button>
          ))}
        </div>
      </div>

      {/* kpi_automation_v1: automation hero */}
      {auto && auto.classification && (
        <div style={{ background:'#fff', borderRadius:16, padding: isMobile?18:24, boxShadow:'0 1px 6px rgba(0,0,0,0.07)', marginBottom: isMobile?16:20, borderLeft:'4px solid #8b5cf6' }}>
          <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'space-between', alignItems:'center', gap:16 }}>
            <div style={{ flex:'1 1 260px' }}>
              <div style={{ fontSize:12, fontWeight:700, letterSpacing:'.04em', color:'#8b5cf6' }}>⚡ ระบบจัดการเองได้กี่ %</div>
              <div style={{ fontSize: isMobile?44:56, fontWeight:800, lineHeight:1, margin:'8px 0 4px', color:'#0f172a' }}>
                {auto.classification.automation_pct}<span style={{ fontSize:22, color:'#94a3b8' }}>%</span>
              </div>
              <div style={{ fontSize:13, color:'#475569' }}>
                บอท/AI แก้จบเองได้ <b style={{color:'#8b5cf6'}}>{Number(auto.classification.automation).toLocaleString()}</b> จาก {Number(auto.classification.total).toLocaleString()} เคส — โดยไม่ต้องใช้แอดมิน
              </div>
              <div style={{ marginTop:16, maxWidth:520 }}>
                <div style={{ height:14, background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:999, overflow:'hidden', position:'relative' }}>
                  <div style={{ height:'100%', width: Math.min(100, auto.classification.automation_pct) + '%', background:'linear-gradient(90deg,#0ea5e9,#8b5cf6)', borderRadius:999 }} />
                  <div style={{ position:'absolute', top:-4, bottom:-4, left:'90%', width:2, background:'#22c55e' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:10.5, color:'#94a3b8', marginTop:6 }}>
                  <span>0%</span><span style={{color:'#16a34a', fontWeight:700}}>เป้าลดภาระแอดมิน → 90%</span><span>100%</span>
                </div>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, minWidth: isMobile?'100%':230 }}>
              {[['ระบบตอบเอง', auto.classification.automation, '#8b5cf6'],['AI + คน', auto.classification.hybrid, '#0ea5e9'],['แอดมินล้วน', auto.classification.human, '#64748b'],['เคสหลุด', auto.classification.dropped, '#ef4444']].map((row) => (
                <div key={row[0]} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontSize:11.5, color:'#64748b', display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{width:8,height:8,borderRadius:2,background:row[2],display:'inline-block'}} />{row[0]}
                  </div>
                  <div style={{ fontSize:20, fontWeight:800, color:row[2], marginTop:2 }}>{Number(row[1]).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
          {auto.classification.ai_deflect > 0 && (
            <div style={{ marginTop:12, fontSize:12, color:'#94a3b8' }}>
              * ไม่นับ {auto.classification.ai_deflect} เคสที่ AI แค่โยนให้เจ้าหน้าที่ (ยังไม่ถือว่า AI แก้จบ)
            </div>
          )}
        </div>
      )}

      {/* accept_v2: grid 2x3 บนมือถือ, 3 cols บน desktop (รวม 6 cards) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: isMobile ? 10 : 16,
        marginBottom: isMobile ? 20 : 28,
      }}>
        <StatCard icon={Users} label="เคสทั้งหมด" value={totalCases} color="#0ea5e9" isMobile={isMobile} trend={auto && auto.trend ? mkTrend(totalCases, auto.trend.total, true) : null} />
        <StatCard icon={CheckCircle} label="ปิดเคสแล้ว" value={totalClosed} color="#22c55e" isMobile={isMobile} />
        <StatCard icon={Zap} label="ปิดไวใน 1 นาที" value={totalFast} color="#f59e0b" sub={`${overallPct}% ของเคสที่ปิด`} isMobile={isMobile} trend={auto && auto.trend ? mkTrend(totalFast, auto.trend.fast, true) : null} />
        <StatCard icon={AlertTriangle} label="เคสหลุด/ไม่ได้ตอบ" value={auto && auto.classification ? auto.classification.dropped : '-'} sub={auto && auto.classification ? auto.classification.dropped_pct + '% ของเคส (ไม่มีใครตอบ)' : ''} color="#ef4444" isMobile={isMobile} trend={auto && auto.trend ? mkTrend(auto.classification.dropped, auto.trend.dropped, false) : null} />
        <StatCard icon={AlertTriangle} label="ตอบช้าเกิน SLA (>3 นาที)" value={auto && auto.sla ? auto.sla.breach : '-'} sub={auto && auto.sla ? auto.sla.breach_pct + '% ของเคส' : ''} color="#f97316" isMobile={isMobile} trend={auto && auto.sla ? mkTrend(auto.sla.breach, auto.sla.prev_breach, false) : null} /> {/* kpi_insights_v1 */}
        {/* accept_v2: 2 cards ใหม่ */}
        <StatCard icon={Timer} label="เวลารับเคสเฉลี่ย" value={_avgAccept != null ? fmtSec(Math.round(_avgAccept)) : '-'} color="#14b8a6" sub="ลูกค้าทัก → รับเคส" isMobile={isMobile} trend={auto && auto.trend && _avgAccept != null ? mkTrend(_avgAccept, auto.trend.avg_accept, false) : null} />
        <StatCard icon={MessageCircle} label="เวลาตอบหลังรับ" value={_avgReplyAfter != null ? fmtSec(Math.round(_avgReplyAfter)) : '-'} color="#6366f1" sub="รับเคส → ตอบครั้งแรก" isMobile={isMobile} trend={auto && auto.trend && _avgReplyAfter != null ? mkTrend(_avgReplyAfter, auto.trend.avg_reply, false) : null} />
      </div>

      {/* kpi_dropowner_v1: who dropped the cases */}
      {auto && auto.dropped_by_agent && auto.dropped_by_agent.length > 0 && (
        <div style={{ background:'#fff', borderRadius:16, padding: isMobile?18:24, boxShadow:'0 1px 6px rgba(0,0,0,0.07)', marginBottom: isMobile?16:20, borderLeft:'4px solid #ef4444' }}>
          <h3 style={{ margin:'0 0 2px', fontSize:16, fontWeight:700, color:'#1e293b' }}>🚨 เคสหลุด — ใครรับผิดชอบ</h3>
          <p style={{ margin:'0 0 14px', fontSize:12.5, color:'#94a3b8' }}>เคสที่มอบหมายให้แล้วแต่ไม่ตอบ · "เปิดอ่านแล้วไม่ตอบ" = เห็นแล้วปล่อยหลุด (สัญญาณชัด) · แถวเหลือง = ไม่ได้มอบหมาย ตกหล่นทั้งทีม</p>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={{ textAlign:'left', padding:'0 12px 10px', fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>แอดมิน</th>
                <th style={{ textAlign:'right', padding:'0 12px 10px', fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>เคสหลุด</th>
                <th style={{ textAlign:'right', padding:'0 12px 10px', fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>เปิดอ่านแล้วไม่ตอบ</th>
              </tr></thead>
              <tbody>
                {auto.dropped_by_agent.map((r, i) => (
                  <tr key={i} style={{ background: r.unassigned ? '#fffbeb' : 'transparent' }}>
                    <td style={{ padding:'11px 12px', borderTop:'1px solid #f1f5f9', fontSize:13, fontWeight: r.unassigned?400:700, color: r.unassigned?'#a16207':'#1e293b' }}>{r.who}</td>
                    <td style={{ padding:'11px 12px', borderTop:'1px solid #f1f5f9', fontSize:14, fontWeight:800, textAlign:'right', color:'#ef4444' }}>{Number(r.dropped).toLocaleString()}</td>
                    <td style={{ padding:'11px 12px', borderTop:'1px solid #f1f5f9', fontSize:13, textAlign:'right' }}>
                      {r.unassigned ? <span style={{ color:'#cbd5e1' }}>—</span> : <span style={{ fontWeight:700, color: Number(r.seen_no_reply)>0 ? '#dc2626' : '#94a3b8' }}>{Number(r.seen_no_reply).toLocaleString()}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* kpi_automation_v1: slip funnel */}
      {auto && auto.slip_funnel && auto.slip_funnel.received === 0 && (
        <div style={{ background:'#fff', borderRadius:16, padding: isMobile?18:24, boxShadow:'0 1px 6px rgba(0,0,0,0.07)', marginBottom: isMobile?16:20 }}>
          <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:700, color:'#1e293b' }}>🧾 เส้นทางสลิปฝาก</h3>
          <p style={{ margin:0, fontSize:13, color:'#94a3b8' }}>ยังไม่มีข้อมูลตรวจสลิปในช่วงเวลานี้ (ร้านนี้อาจใช้โหมด manual หรือยังไม่มีสลิปเข้า)</p>
        </div>
      )}
      {auto && auto.slip_funnel && auto.slip_funnel.received > 0 && (
        <div style={{ background:'#fff', borderRadius:16, padding: isMobile?18:24, boxShadow:'0 1px 6px rgba(0,0,0,0.07)', marginBottom: isMobile?16:28 }}>
          <h3 style={{ margin:'0 0 2px', fontSize:16, fontWeight:700, color:'#1e293b' }}>🧾 เส้นทางสลิปฝาก</h3>
          <p style={{ margin:'0 0 16px', fontSize:12.5, color:'#94a3b8' }}>
            ตรวจสลิปอัตโนมัติ {auto.slip_funnel.auto_verified_pct}% · ยอดตรง {auto.slip_funnel.success_pct}% · บัญชีไม่ตรง {auto.slip_funnel.mismatch_pct}%
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[['สลิปเข้า', auto.slip_funnel.received, '#0ea5e9'],['✅ ยอดตรง', auto.slip_funnel.success, '#22c55e'],['⚠️ บัญชีไม่ตรง', auto.slip_funnel.mismatch, '#f59e0b'],['🔁 สลิปซ้ำ', auto.slip_funnel.duplicate, '#a855f7'],['🚫 อ่านไม่ออก', auto.slip_funnel.invalid, '#ef4444']].map((row) => {
              const pct = auto.slip_funnel.received > 0 ? Math.round(row[1]/auto.slip_funnel.received*1000)/10 : 0;
              return (
                <div key={row[0]} style={{ display:'grid', gridTemplateColumns: isMobile?'96px 1fr 42px':'130px 1fr 48px', alignItems:'center', gap:10 }}>
                  <div style={{ fontSize:12.5, color:'#475569', fontWeight:600 }}>{row[0]}</div>
                  <div style={{ height:26, background:'#f1f5f9', borderRadius:7, overflow:'hidden' }}>
                    <div style={{ height:'100%', width: Math.max(2, pct) + '%', background:row[2], borderRadius:7, display:'flex', alignItems:'center', paddingLeft:10, color:'#fff', fontWeight:800, fontSize:12 }}>{Number(row[1]).toLocaleString()}</div>
                  </div>
                  <div style={{ fontSize:11.5, color:'#94a3b8', fontWeight:700, textAlign:'right' }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* kpi_insights_v1: demand vs staffing */}
      {auto && auto.demand && auto.demand.some(d => d.customer > 0) && (() => {
        const maxV = Math.max(1, ...auto.demand.map(d => Math.max(d.customer, d.agent)));
        const W = 480, H = 150;
        const x = (h) => (h / 23) * W;
        const yC = (v) => H - (v / maxV) * H;
        const custPts = auto.demand.map(d => x(d.hour) + ',' + yC(d.customer)).join(' ');
        const agentPts = auto.demand.map(d => x(d.hour) + ',' + yC(d.agent)).join(' ');
        const gap = auto.demand.filter(d => d.customer > d.agent * 1.6 && d.customer >= maxV * 0.35);
        return (
          <div style={{ background:'#fff', borderRadius:16, padding: isMobile?18:24, boxShadow:'0 1px 6px rgba(0,0,0,0.07)', marginBottom: isMobile?16:28 }}>
            <h3 style={{ margin:'0 0 2px', fontSize:16, fontWeight:700, color:'#1e293b' }}>📈 ดีมานด์ลูกค้า vs กำลังแอดมิน</h3>
            <p style={{ margin:'0 0 14px', fontSize:12.5, color:'#94a3b8' }}>เทียบชั่วโมงที่ลูกค้าทักเข้ามา กับที่แอดมินตอบ (24 ชม.) — ชั่วโมงที่ลูกค้าพุ่งแต่แอดมินน้อย = ควรเพิ่มคน</p>
            <div style={{ overflowX:'auto' }}>
              <svg viewBox={'0 0 ' + W + ' ' + (H + 22)} preserveAspectRatio="none" style={{ width:'100%', minWidth: 420, height: 172 }}>
                <line x1="0" y1={H} x2={W} y2={H} stroke="#e2e8f0" />
                <polygon points={'0,' + H + ' ' + custPts + ' ' + W + ',' + H} fill="rgba(14,165,233,0.14)" />
                <polyline points={custPts} fill="none" stroke="#0ea5e9" strokeWidth="2.5" />
                <polyline points={agentPts} fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeDasharray="6 4" />
                {[0,6,12,18,23].map(h => (<text key={h} x={x(h)} y={H + 16} fontSize="10" fill="#94a3b8" textAnchor="middle">{String(h).padStart(2,'0')}</text>))}
              </svg>
            </div>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:12, color:'#475569', marginTop:8 }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><i style={{width:12,height:12,borderRadius:3,background:'rgba(14,165,233,0.5)',display:'inline-block'}} /> ลูกค้าทัก</span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><i style={{width:16,height:3,background:'#8b5cf6',display:'inline-block'}} /> แอดมินตอบ</span>
              {gap.length > 0 && <span style={{ color:'#dc2626', fontWeight:600 }}>🔴 ช่วงคนไม่พอ: {gap.map(g => String(g.hour).padStart(2,'0')).join(', ')} น.</span>}
            </div>
          </div>
        );
      })()}

      {/* timeline_v1: Bar Chart + Heatmap */}
      {timeline?.stats?.length > 0 && (
        <>
          {/* Bar Chart: เปรียบเทียบ 3 metrics ต่อคน */}
          <div style={{
            background: '#fff', borderRadius: 14,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            padding: isMobile ? '14px 12px' : '20px 24px',
            marginBottom: isMobile ? 16 : 24,
          }}>
            <h2 style={{
              margin: '0 0 14px', fontSize: isMobile ? 14 : 15,
              fontWeight: 600, color: '#1e293b',
            }}>📊 เปรียบเทียบประสิทธิภาพรายแอดมิน</h2>
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
              <BarChart data={timeline.stats} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <XAxis dataKey="agent_name" tick={{ fontSize: isMobile ? 10 : 12 }} />
                <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 12 }} />
                <Bar dataKey="closed" name="ปิดเคส" fill="#22c55e" radius={[4,4,0,0]} />
                <Bar dataKey="fast_close" name="ปิดไว 1 นาที" fill="#f59e0b" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Heatmap: agent x 24 hours */}
          <div style={{
            background: '#fff', borderRadius: 14,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            padding: isMobile ? '14px 12px' : '20px 24px',
            marginBottom: isMobile ? 16 : 24,
            overflowX: 'auto',
          }}>
            <h2 style={{
              margin: '0 0 14px', fontSize: isMobile ? 14 : 15,
              fontWeight: 600, color: '#1e293b',
            }}>🕐 ช่วงเวลาทำงาน (24 ชั่วโมง)</h2>
            <div style={{ minWidth: isMobile ? 480 : 'auto' }}>
              {(() => {
                // Build matrix: agentId -> hour -> count
                const matrix = {};
                let maxCount = 1;
                (timeline.heatmap || []).forEach(r => {
                  if (!matrix[r.agent_id]) matrix[r.agent_id] = {};
                  matrix[r.agent_id][r.hour] = r.msg_count;
                  if (r.msg_count > maxCount) maxCount = r.msg_count;
                });
                const cellSize = isMobile ? 14 : 22;
                const cellGap = 2;
                const labelWidth = isMobile ? 50 : 80;
                return (
                  <>
                    {/* Hour header */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ width: labelWidth, flexShrink: 0 }} />
                      <div style={{ display: 'flex', gap: cellGap, flex: 1 }}>
                        {Array.from({length: 24}, (_, h) => (
                          <div key={h} style={{
                            width: cellSize, textAlign: 'center',
                            fontSize: isMobile ? 8 : 10, color: '#94a3b8',
                          }}>{h % 6 === 0 ? h : ''}</div>
                        ))}
                      </div>
                    </div>
                    {/* Rows per agent */}
                    {timeline.stats.map(s => (
                      <div key={s.agent_id} style={{
                        display: 'flex', alignItems: 'center', marginBottom: cellGap,
                      }}>
                        <div style={{
                          width: labelWidth, flexShrink: 0,
                          fontSize: isMobile ? 11 : 13, fontWeight: 600,
                          color: '#1e293b', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          paddingRight: 8,
                        }}>{s.agent_name}</div>
                        <div style={{ display: 'flex', gap: cellGap, flex: 1 }}>
                          {Array.from({length: 24}, (_, h) => {
                            const count = matrix[s.agent_id]?.[h] || 0;
                            const intensity = count / maxCount;
                            const bg = count === 0
                              ? '#f1f5f9'
                              : `rgba(14, 165, 233, ${0.15 + intensity * 0.85})`;
                            return (
                              <div key={h} title={`${s.agent_name} ${h}:00 — ${count} ข้อความ`}
                                style={{
                                  width: cellSize, height: cellSize,
                                  background: bg, borderRadius: 3,
                                  cursor: count > 0 ? 'pointer' : 'default',
                                }} />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {/* Legend */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      marginTop: 12, fontSize: isMobile ? 10 : 11, color: '#64748b',
                    }}>
                      <span>น้อย</span>
                      {[0.2, 0.4, 0.6, 0.8, 1.0].map(v => (
                        <div key={v} style={{
                          width: 16, height: 12,
                          background: `rgba(14, 165, 233, ${0.15 + v * 0.85})`,
                          borderRadius: 2,
                        }} />
                      ))}
                      <span>มาก</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Agents — Card list บน mobile / Table บน desktop */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: isMobile ? '14px 16px' : '16px 24px',
          borderBottom: '1px solid #f1f5f9',
        }}>
          <h2 style={{
            margin: 0,
            fontSize: isMobile ? 14 : 15,
            fontWeight: 600,
            color: '#1e293b',
          }}>รายละเอียดรายแอดมิน</h2>
        </div>

        {agents.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? 30 : 40,
            color: '#94a3b8',
            fontSize: 14,
          }}>ไม่มีข้อมูลในช่วงนี้</div>
        ) : isMobile ? (
          /* mobile_v1: Card list view */
          <div>
            {agents.map((a, i) => (
              <AgentCard key={a.agent_id} agent={a} idx={i} />
            ))}
          </div>
        ) : (
          /* Desktop: คงตารางเดิม */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'แอดมิน', 'รับเคส', 'ตอบแล้ว', 'ปิดเคส', 'ปิดไวใน 1 นาที', '% สำเร็จ', 'รับเคสไว', 'ตอบไวหลังรับ', 'เวลาตอบเฉลี่ย'].map((h, i) => (
                  <th key={i} style={{ padding: '12px 16px', textAlign: i > 1 ? 'center' : 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map((a, i) => {
                const pct = Number(a.fast_close_pct);
                const pctColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
                return (
                  <tr key={a.agent_id} style={{ borderTop: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '14px 16px', fontSize: 16 }}>{medal(i)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>{a.agent_name}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{a.email}</div>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, fontSize: 16, color: '#0ea5e9' }}>{a.total_cases}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b' }}>{a.replied_cases}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', color: '#22c55e', fontWeight: 600 }}>{a.closed_cases}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{ background: '#fef9c3', color: '#854d0e', fontWeight: 700, borderRadius: 6, padding: '3px 10px', fontSize: 14 }}>
                        ⚡ {a.fast_close_cases}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontWeight: 700, color: pctColor, fontSize: 15 }}>{pct.toFixed(1)}%</span>
                        <div style={{ width: 60, height: 4, background: '#f1f5f9', borderRadius: 2 }}>
                          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pctColor, borderRadius: 2 }} />
                        </div>
                      </div>
                    </td>
                    {/* accept_v2: 2 cells ใหม่ */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0d9488', fontSize: 13 }}>
                        <Timer size={13} />{Number(a.avg_accept_sec) > 0 ? fmtSec(Math.round(Number(a.avg_accept_sec))) : '-'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#4f46e5', fontSize: 13 }}>
                        <MessageCircle size={13} />{Number(a.avg_reply_after_accept_sec) > 0 ? fmtSec(Math.round(Number(a.avg_reply_after_accept_sec))) : '-'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 13 }}>
                        <Clock size={13} />{fmtSec(a.avg_response_sec)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Legend — stack แนวตั้งบนมือถือ */}
      <div style={{
        marginTop: 16,
        fontSize: isMobile ? 12 : 12,
        color: '#94a3b8',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 6 : 20,
        background: isMobile ? '#fff' : 'transparent',
        padding: isMobile ? '12px 14px' : 0,
        borderRadius: isMobile ? 10 : 0,
        boxShadow: isMobile ? '0 1px 4px rgba(0,0,0,0.05)' : 'none',
      }}>
        <span>🟢 ≥80% ดีมาก</span>
        <span>🟡 50–79% พอใช้</span>
        <span>🔴 &lt;50% ต้องปรับปรุง</span>
        <span>⚡ = ปิดเคสภายใน 1 นาทีนับจากข้อความแรกของลูกค้า</span>
      </div>
    </div>
  );
}
